/**
 * Adapter layer that maps GraphQL response types into flat view-model types
 * consumed by rescue portal UI components.
 *
 * This centralizes all API ↔ UI data-shape knowledge so component templates
 * don't need to reach into nested GraphQL structures.
 */

import { DateTime } from 'luxon';
import * as gqlTypes from '@/types/gql-op-types';
import type { ExternalChipLookupData } from './rescueQueries';
import type { PetFormData } from '../pages/Chips/RegisterChipModal';
import type { AdopterFormData } from '../pages/Chips/TransferChipModal';

// ── View-model types ─────────────────────────

/** Flat chip type used by all rescue portal UI components. */
export interface RescueChip {
  /** GraphQL opaque ID — used for mutations. */
  _graphqlId: string;
  /** Short human-readable ID (e.g. "FN-900001"). */
  id: string;
  /** Full 15-digit serial number. */
  fullChipId: string;
  status: 'stock' | 'active';
  /** Empty string when unknown. */
  location: string;
  batchDate: string;
  assignedTo: 'rescue' | 'adopter';
  petName: string | null;
  species: string | null;
  breed: string | null;
  mixedBreed: boolean;
  gender: string | null;
  dob: string | null;
  dobEstimated: boolean;
  spayedNeutered: string | null;
  color: string | null;
  weight: number | null;
  additionalInfo: string | null;
  adopterFirstName: string | null;
  adopterLastName: string | null;
  adopterEmail: string | null;
  adopterPhone: string | null;
  adopterStreet: string | null;
  adopterApt: string | null;
  adopterCity: string | null;
  adopterState: string | null;
  adopterZip: string | null;
  registrationSource: string | null;
  registeredDate: string | null;
  lastUpdated: string;
  history: RescueChipHistoryEntry[];
}

export interface RescueChipHistoryEntry {
  date: string;
  event: 'registered' | 'owner_added' | 'owner_cleared' | 'transferred_to_org' | 'reclaimed';
  detail: string;
  source?: string;
  contactName?: string;
  contactEmail?: string;
  contactPhone?: string;
}

/** Flat external chip type used by pet-lookup UI components. */
export interface RescueExternalChip {
  /** GraphQL opaque ID — used for fetching details on own chips. */
  _graphqlId: string;
  /** Whether this chip belongs to the current reseller. */
  isOwn: boolean;
  id: string;
  fullChipId: string;
  status: 'active';
  assignedTo: 'rescue' | 'adopter';
  petName: string | null;
  species: string | null;
  breed: string | null;
  mixedBreed: boolean;
  gender: string | null;
  dob: string | null;
  dobEstimated: boolean;
  spayedNeutered: string | null;
  color: string | null;
  weight: number | null;
  additionalInfo: string | null;
  ownerName: string | null;
  ownerEmail: string | null;
  ownerPhone: string | null;
  ownerStreet: string | null;
  ownerApt: string | null;
  ownerCity: string | null;
  ownerState: string | null;
  ownerZip: string | null;
  organization: string;
  registeredDate: string;
  lastUpdated: string;
  history: RescueChipHistoryEntry[];
}

// ── Helpers ──────────────────────────────────

const KG_TO_LBS = 2.20462;

function formatDobFromParts(year?: number | null, month?: number | null, day?: number | null): string | null {
  if (!year) return null;
  const y = String(year);
  const m = month ? String(month).padStart(2, '0') : '01';
  const d = day ? String(day).padStart(2, '0') : '01';
  return `${y}-${m}-${d}`;
}

function kgToLbs(kg: number | null | undefined): number | null {
  if (kg == null || kg === 0) return null;
  return Math.round(kg * KG_TO_LBS * 10) / 10;
}

function genderToDisplay(gender: gqlTypes.Gender | null | undefined): string | null {
  if (!gender) return null;
  if (gender === gqlTypes.Gender.MALE) return 'Male';
  if (gender === gqlTypes.Gender.FEMALE) return 'Female';
  return null;
}

function speciesToDisplay(species: gqlTypes.Species | null | undefined): string | null {
  if (!species) return null;
  if (species === gqlTypes.Species.DOG) return 'Dog';
  if (species === gqlTypes.Species.CAT) return 'Cat';
  return 'Other';
}

function toIsoDate(dt: string | null | undefined): string {
  if (!dt) return '';
  const parsed = DateTime.fromISO(dt);
  return parsed.isValid ? parsed.toISODate()! : '';
}

function sourceToDisplay(source: string | null | undefined): string | null {
  if (!source) return null;
  if (source.toLowerCase().startsWith('shelterluv')) return 'ShelterLuv';
  if (source === 'FI_NANO') return 'Nano';
  return source;
}

function deriveAssignedTo(contactEmail: string | null | undefined, resellerEmail: string): 'rescue' | 'adopter' {
  if (!contactEmail) return 'rescue';
  if (contactEmail.toLowerCase() === resellerEmail.toLowerCase()) return 'rescue';
  return 'adopter';
}

// ── Chip type from miniChipFragment (list views) ─

type MiniChipData = gqlTypes.viewingChips['currentUser']['chipReseller'] extends infer R
  ? R extends { chips: { chips: Array<infer C> } }
    ? C
    : never
  : never;

/** Extended with extra fields from rescueViewingChipsQuery. */
type RescueMiniChipData = MiniChipData & {
  createdAt?: string | null;
  latestContactHistoryEntry?: { contactInfo?: { primaryEmail?: string | null } } | null;
};

export function mapMiniChipToRescueChip(chip: RescueMiniChipData, resellerEmail: string): RescueChip {
  const status = chip.status === gqlTypes.ChipStatusType.SHIPPED ? 'stock' : 'active';
  const contactEmail = chip.latestContactHistoryEntry?.contactInfo?.primaryEmail;
  const assignedTo = deriveAssignedTo(contactEmail, resellerEmail);
  const createdIso = toIsoDate(chip.createdAt);
  const updatedIso = toIsoDate(chip.updatedAt);

  return {
    _graphqlId: chip.id,
    id: chip.shortId,
    fullChipId: chip.serialNumber,
    status,
    location: '',
    batchDate: status === 'stock' ? createdIso : '',
    assignedTo,
    petName: chip.pet?.name ?? null,
    species: speciesToDisplay(chip.pet?.species),
    breed: chip.pet?.breed?.name ?? null,
    mixedBreed: chip.pet ? !chip.pet.isPurebred : false,
    gender: genderToDisplay(chip.pet?.gender),
    dob: formatDobFromParts(chip.pet?.yearOfBirth, chip.pet?.monthOfBirth, chip.pet?.dayOfBirth),
    dobEstimated: false,
    spayedNeutered: null,
    color: chip.pet?.color ?? null,
    weight: kgToLbs(chip.pet?.weight),
    additionalInfo: chip.pet?.additionalInfo ?? null,
    adopterFirstName: chip.latestContactHistoryEntry?.contactInfo?.firstName ?? null,
    adopterLastName: chip.latestContactHistoryEntry?.contactInfo?.lastName ?? null,
    adopterEmail: null,
    adopterPhone: null,
    adopterStreet: null,
    adopterApt: null,
    adopterCity: null,
    adopterState: null,
    adopterZip: null,
    registrationSource: sourceToDisplay(chip.latestContactHistoryEntry?.source),
    registeredDate: status === 'active' ? updatedIso : null,
    lastUpdated: updatedIso,
    history:
      status === 'active'
        ? [
            {
              date: updatedIso,
              event: 'registered' as const,
              detail: 'Registered.',
              source: sourceToDisplay(chip.latestContactHistoryEntry?.source) ?? undefined,
            },
          ]
        : [],
  };
}

// ── Contact history → event mapping ────────────

/** Shape of a contactHistory entry returned by the resellerChip query. */
interface ContactHistoryEntryGql {
  id: string;
  source: string;
  latest: boolean;
  rejected: boolean;
  createdAt: string;
  contactInfo: {
    firstName?: string | null;
    lastName?: string | null;
    primaryEmail?: string | null;
    primaryPhone?: string | null;
  };
}

const HISTORY_EVENT_LABELS: Record<RescueChipHistoryEntry['event'], string> = {
  registered: 'Registered',
  owner_added: 'Owner Added',
  owner_cleared: 'Owner Cleared',
  transferred_to_org: 'Transferred to Org',
  reclaimed: 'Reclaimed',
};

function deriveHistoryEvent(
  entry: ContactHistoryEntryGql,
  isOldest: boolean,
  resellerEmail: string,
): RescueChipHistoryEntry['event'] {
  if (isOldest) {
    return 'registered';
  }
  // An entry represents "owner cleared" when the contact was explicitly rejected,
  // when there's no email (contact info was wiped), or when the email matches
  // the rescue org — meaning the chip was returned to the rescue's own account.
  const email = entry.contactInfo.primaryEmail?.toLowerCase() ?? '';
  if (entry.rejected || !email || email === resellerEmail.toLowerCase()) {
    return 'owner_cleared';
  }
  return 'owner_added';
}

function buildHistoryDetail(event: RescueChipHistoryEntry['event'], entry: ContactHistoryEntryGql): string {
  const name = [entry.contactInfo.firstName, entry.contactInfo.lastName].filter(Boolean).join(' ');

  if (event === 'registered') {
    return name ? `Registered to ${name}.` : 'Registered.';
  }
  if (event === 'owner_added') {
    return name ? `Transferred to ${name}.` : 'Transferred to new owner.';
  }
  if (event === 'owner_cleared') {
    return name ? `Removed ${name}. Pet returned to rescue.` : 'Owner removed. Pet returned to rescue.';
  }
  if (event === 'reclaimed') {
    return name ? `Reclaimed from ${name}.` : 'Reclaimed.';
  }
  return HISTORY_EVENT_LABELS[event];
}

function mapContactHistoryToEvents(entries: ContactHistoryEntryGql[], resellerEmail: string): RescueChipHistoryEntry[] {
  if (!entries.length) {
    return [];
  }

  return entries.map((entry, index) => {
    const isOldest = index === entries.length - 1;
    const event = deriveHistoryEvent(entry, isOldest, resellerEmail);
    return {
      date: toIsoDate(entry.createdAt),
      event,
      detail: buildHistoryDetail(event, entry),
      source: sourceToDisplay(entry.source) ?? undefined,
      contactName: [entry.contactInfo.firstName, entry.contactInfo.lastName].filter(Boolean).join(' ') || undefined,
      contactEmail: entry.contactInfo.primaryEmail ?? undefined,
      contactPhone: entry.contactInfo.primaryPhone ?? undefined,
    };
  });
}

// ── Chip type from resellerChipFragment (detail view) ─

type ResellerChipData = NonNullable<gqlTypes.resellerChipDetails['resellerChip']>;

export function mapResellerChipToRescueChip(chip: ResellerChipData, resellerEmail: string): RescueChip {
  const status = chip.status === gqlTypes.ChipStatusType.SHIPPED ? 'stock' : 'active';
  const latestEntry = chip.latestContactHistoryEntry ?? null;
  const contactInfo = latestEntry?.contactInfo;
  const assignedTo = deriveAssignedTo(contactInfo?.primaryEmail, resellerEmail);
  const updatedIso = toIsoDate(chip.updatedAt);

  const historyFromApi = mapContactHistoryToEvents(
    (chip as ResellerChipData & { contactHistory?: ContactHistoryEntryGql[] }).contactHistory ?? [],
    resellerEmail,
  );

  // If `contactHistory` is empty but a `latestContactHistoryEntry` snapshot exists,
  // synthesize a fallback entry from it so the History button still appears.
  let history = historyFromApi;
  if (history.length === 0 && latestEntry) {
    const event: RescueChipHistoryEntry['event'] = assignedTo === 'adopter' ? 'owner_added' : 'registered';
    const name = [contactInfo?.firstName, contactInfo?.lastName].filter(Boolean).join(' ');
    history = [
      {
        date: updatedIso,
        event,
        detail:
          event === 'owner_added'
            ? name
              ? `Transferred to ${name}.`
              : 'Transferred to new owner.'
            : name
              ? `Registered to ${name}.`
              : 'Registered.',
        source: sourceToDisplay(latestEntry.source) ?? undefined,
        contactName: name || undefined,
        contactEmail: contactInfo?.primaryEmail ?? undefined,
        contactPhone: contactInfo?.primaryPhone ?? undefined,
      },
    ];
  }

  return {
    _graphqlId: chip.id,
    id: chip.shortId,
    fullChipId: chip.serialNumber,
    status,
    location: '',
    batchDate: status === 'stock' ? updatedIso : '',
    assignedTo,
    petName: chip.pet?.name ?? null,
    species: speciesToDisplay(chip.pet?.species),
    breed: chip.pet?.breed?.name ?? null,
    mixedBreed: chip.pet ? !chip.pet.isPurebred : false,
    gender: genderToDisplay(chip.pet?.gender),
    dob: formatDobFromParts(chip.pet?.yearOfBirth, chip.pet?.monthOfBirth, chip.pet?.dayOfBirth),
    dobEstimated: false,
    spayedNeutered: null,
    color: chip.pet?.color ?? null,
    weight: kgToLbs(chip.pet?.weight),
    additionalInfo: chip.pet?.additionalInfo ?? null,
    adopterFirstName: contactInfo?.firstName ?? null,
    adopterLastName: contactInfo?.lastName ?? null,
    adopterEmail: contactInfo?.primaryEmail ?? null,
    adopterPhone: contactInfo?.primaryPhone ?? null,
    adopterStreet: contactInfo?.line1 ?? null,
    adopterApt: contactInfo?.line2 ?? null,
    adopterCity: contactInfo?.city ?? null,
    adopterState: contactInfo?.state ?? null,
    adopterZip: contactInfo?.zipcode ?? null,
    registrationSource: sourceToDisplay(latestEntry?.source),
    registeredDate: status === 'active' ? updatedIso : null,
    lastUpdated: updatedIso,
    history,
  };
}

// ── External chip type from baseChipFragment (pet lookup) ─

type BaseChipData = NonNullable<gqlTypes.chipDetails['chip']>;

export function mapBaseChipToExternalChip(chip: BaseChipData): RescueExternalChip {
  const updatedIso = chip.__typename === 'AssignedChip' ? toIsoDate(chip.pet?.updatedAt) : '';
  const isAssigned = chip.__typename === 'AssignedChip';

  const pet = isAssigned ? chip.pet : null;
  const contactInfo = isAssigned ? chip.latestContactHistoryEntry?.contactInfo : null;
  const hasOwner = isAssigned
    ? !!chip.latestContactHistoryEntry
    : chip.__typename === 'StrangerChip'
      ? chip.hasOwner
      : false;

  return {
    _graphqlId: chip.id,
    isOwn: isAssigned,
    id: chip.shortId,
    fullChipId: chip.serialNumber,
    status: 'active',
    assignedTo: hasOwner ? 'adopter' : 'rescue',
    petName: pet?.name ?? null,
    species: speciesToDisplay(pet?.species),
    breed: pet?.breed?.name ?? null,
    mixedBreed: pet ? !pet.isPurebred : false,
    gender: genderToDisplay(pet?.gender),
    dob: formatDobFromParts(pet?.yearOfBirth, pet?.monthOfBirth, pet?.dayOfBirth),
    dobEstimated: false,
    spayedNeutered: null,
    color: pet?.color ?? null,
    weight: kgToLbs(pet?.weight),
    additionalInfo: pet?.additionalInfo ?? null,
    ownerName: contactInfo ? `${contactInfo.firstName}${contactInfo.lastName ? ` ${contactInfo.lastName}` : ''}` : null,
    ownerEmail: contactInfo?.primaryEmail ?? null,
    ownerPhone: contactInfo?.primaryPhone ?? null,
    ownerStreet: contactInfo?.line1 ?? null,
    ownerApt: contactInfo?.line2 ?? null,
    ownerCity: contactInfo?.city ?? null,
    ownerState: contactInfo?.state ?? null,
    ownerZip: contactInfo?.zipcode ?? null,
    organization: isAssigned ? 'Owned by you' : '',
    registeredDate: updatedIso || new Date().toISOString().split('T')[0],
    lastUpdated: updatedIso || new Date().toISOString().split('T')[0],
    history: [],
  };
}

// ── External chip from resellerChip query (verified rescue lookup) ─

type ExternalResellerChipData = NonNullable<ExternalChipLookupData['resellerChip']>;

export function mapLookupChipToExternalChip(chip: ExternalResellerChipData): RescueExternalChip {
  const updatedIso = toIsoDate(chip.updatedAt);
  const contactInfo = chip.latestContactHistoryEntry?.contactInfo;
  const hasOwner = !!contactInfo;

  return {
    _graphqlId: chip.id,
    isOwn: false,
    id: chip.shortId,
    fullChipId: chip.serialNumber,
    status: 'active',
    assignedTo: hasOwner ? 'adopter' : 'rescue',
    petName: chip.pet?.name ?? null,
    species: speciesToDisplay(chip.pet?.species as gqlTypes.Species | null | undefined),
    breed: chip.pet?.breed?.name ?? null,
    mixedBreed: chip.pet ? !chip.pet.isPurebred : false,
    gender: genderToDisplay(chip.pet?.gender as gqlTypes.Gender | null | undefined),
    dob: formatDobFromParts(chip.pet?.yearOfBirth, chip.pet?.monthOfBirth, chip.pet?.dayOfBirth),
    dobEstimated: false,
    spayedNeutered: null,
    color: chip.pet?.color ?? null,
    weight: kgToLbs(chip.pet?.weight),
    additionalInfo: chip.pet?.additionalInfo ?? null,
    ownerName: contactInfo ? `${contactInfo.firstName}${contactInfo.lastName ? ` ${contactInfo.lastName}` : ''}` : null,
    ownerEmail: contactInfo?.primaryEmail ?? null,
    ownerPhone: contactInfo?.primaryPhone ?? null,
    ownerStreet: contactInfo?.line1 ?? null,
    ownerApt: contactInfo?.line2 ?? null,
    ownerCity: contactInfo?.city ?? null,
    ownerState: contactInfo?.state ?? null,
    ownerZip: contactInfo?.zipcode ?? null,
    organization: chip.reseller?.name ?? 'External',
    registeredDate: updatedIso || new Date().toISOString().split('T')[0],
    lastUpdated: updatedIso || new Date().toISOString().split('T')[0],
    history: [],
  };
}

// ── Form data → GraphQL input mappers ────────

function speciesFormToGql(species: string): gqlTypes.Species | undefined {
  if (species === 'dog') return gqlTypes.Species.DOG;
  if (species === 'cat') return gqlTypes.Species.CAT;
  if (species === 'other') return gqlTypes.Species.OTHER;
  return undefined;
}

function genderFormToGql(gender: string): gqlTypes.Gender {
  if (gender === 'female') return gqlTypes.Gender.FEMALE;
  return gqlTypes.Gender.MALE;
}

export function mapPetFormToChipNewPetInput(petData: PetFormData, breedId?: string | null): gqlTypes.ChipNewPetInput {
  return {
    name: petData.petName,
    gender: genderFormToGql(petData.gender),
    species: speciesFormToGql(petData.species),
    breedId: breedId ?? undefined,
    isPurebred: !petData.mixedBreed,
    yearOfBirth: parseInt(petData.dobYear, 10),
    monthOfBirth: petData.dobMonth ? parseInt(petData.dobMonth, 10) : undefined,
    dayOfBirth: petData.dobDay ? parseInt(petData.dobDay, 10) : undefined,
    color: petData.color || undefined,
    weight: petData.weight ? parseFloat(petData.weight) / KG_TO_LBS : 0,
    additionalInfo: petData.additionalInfo || undefined,
  };
}

export function mapAdopterFormToContactInfoInput(adopter: AdopterFormData): gqlTypes.ChipContactInfoInput {
  return {
    firstName: adopter.firstName,
    lastName: adopter.lastName || undefined,
    primaryEmail: adopter.email,
    primaryPhone: adopter.phone || undefined,
    line1: adopter.street || undefined,
    line2: adopter.apt || undefined,
    city: adopter.city || undefined,
    state: adopter.state || undefined,
    zipcode: adopter.zip || undefined,
    country: 'US',
  };
}
